import React, { useMemo, useState } from 'react'
import {
	Box,
	Dialog,
	IconButton,
	MenuItem,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import { type GridColDef } from '@mui/x-data-grid-pro'
import { AnimatedDataGridPro, useDataGridAnimations, type DensityMode } from '../../CustomDataGrid'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { Dayjs } from 'dayjs'
import CustomButton from '../../CustomButton/CustomButton'
import CustomColumnMenu, { columnMenuPopperSx } from '../../CustomDataGrid/CustomColumnMenu'
import { popupActionButtonSx, popupSecondaryButtonSx } from '../popupButtonStyles'
import { calendarInputSx, calendarPopperSlotProps, popupInputLabelSx } from '../popupInputStyles'
import { useModal } from '../../../../../store/ModalProvider'
import type { ServicesByLocalityModalData, ServicesByLocalityRow } from '../../../../../types/modal'

// Invisible but clickable opener so picker still opens when clicked
const EmptyButton: React.ComponentType<any> = (props: any) => (
	<IconButton
		{...props}
		sx={{ ...(props?.sx || {}), opacity: 0 }}
	/>
)

const dialogShadow =
	'0px 9px 46px 8px rgba(9, 32, 86, 0.12), 0px 24px 38px 3px rgba(9, 32, 86, 0.14), 0px 11px 15px -7px rgba(9, 32, 86, 0.2)'

const inputSx = {
	'& .MuiInputLabel-root': {
		...popupInputLabelSx,
	},
	'& .MuiOutlinedInput-root': {
		backgroundColor: '#FFFFFF',
		borderRadius: '4px',
		minHeight: '40px',
		fontSize: '13px',
		fontWeight: 500,
		color: '#092056',
		'& fieldset': {
			borderColor: 'rgba(134, 149, 175, 0.3)',
		},
		'&:hover fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.4)',
		},
		'&.Mui-focused fieldset': {
			borderColor: 'rgba(0, 165, 255, 0.6)',
		},
	},
	'& .MuiOutlinedInput-input': {
		padding: '8px 12px',
		lineHeight: 1.3,
	},
	'& .MuiSelect-select': {
		padding: '8px 36px 8px 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiSelect-icon': {
		color: '#00A5FF',
		right: '10px',
	},
}

const toggleGroupSx = {
	border: '1px solid rgba(134, 149, 175, 0.15)',
	borderRadius: '4px',
	'& .MuiToggleButton-root': {
		border: 'none',
		borderRadius: 0,
		padding: '4.8px',
		'&:not(:last-child)': {
			borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
		},
		'&.Mui-selected': {
			backgroundColor: '#F5F7FC',
			'&:hover': {
				backgroundColor: '#F5F7FC',
			},
		},
	},
}

const tableSx = {
	border: 'none',
	'--DataGrid-rowBorderColor': 'transparent',
	'& .MuiDataGrid-columnHeaders': {
		backgroundColor: '#FFFFFF',
		borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
		borderTop: 'none',
		boxShadow: 'none',
	},
	'& .MuiDataGrid-columnHeaderTitle': {
		fontFamily: 'Poppins, sans-serif',
		fontWeight: 600,
		fontSize: '12px',
		color: '#8695AF',
		letterSpacing: '0.17px',
		lineHeight: '24px',
		textTransform: 'none',
	},
	'& .MuiDataGrid-columnSeparator': {
		opacity: 0,
		color: 'rgba(134, 149, 175, 0.6)',
		transition: 'opacity 0.2s ease',
	},
	'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
		opacity: 1,
	},
	'& .MuiDataGrid-columnSeparator--resizable': {
		cursor: 'col-resize',
	},
	'& .MuiDataGrid-columnHeader': {
		borderRight: 'none',
	},
	'& .MuiDataGrid-cell': {
		fontFamily: 'Poppins, sans-serif',
		fontSize: '12px',
		color: '#092056',
		borderBottom: 'none',
		borderRight: 'none',
	},
	'& .MuiDataGrid-row': {
		borderBottom: 'none',
	},
	'& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': {
		outline: 'none',
	},
}

const servicesTableSx = {
	'& .MuiDataGrid-columnHeader': {
		padding: '0 12px',
	},
	'& .MuiDataGrid-cell': {
		padding: '0 12px',
		display: 'flex',
		alignItems: 'center',
	},
	'& .MuiDataGrid-row:hover': {
		backgroundColor: 'rgba(0, 165, 255, 0.04)',
	},
}

const defaultRows: ServicesByLocalityRow[] = [
	{
		id: '1',
		requisicao: '95509',
		dataInicio: '02/05/2024',
		horaInicio: '08:00',
		localInicio: 'Lisboa',
		localTermino: 'Faro',
		horaTermino: '11:00',
		dataTermino: '02/05/2024',
		motorista: 'João Oliveira',
		cliente: 'Best Transfers 4 U',
	},
	{
		id: '2',
		requisicao: '84211',
		dataInicio: '02/05/2024',
		horaInicio: '09:15',
		localInicio: 'Lisboa',
		localTermino: 'Sintra',
		horaTermino: '10:10',
		dataTermino: '02/05/2024',
		motorista: 'Ana Martins',
		cliente: 'Transol Corporate',
	},
	{
		id: '3',
		requisicao: '84212',
		dataInicio: '02/05/2024',
		horaInicio: '10:30',
		localInicio: 'Setúbal',
		localTermino: 'Lisboa',
		horaTermino: '11:30',
		dataTermino: '02/05/2024',
		motorista: 'Rui Silva',
		cliente: 'Scenic Tours',
	},
	{
		id: '4',
		requisicao: '84213',
		dataInicio: '02/05/2024',
		horaInicio: '12:00',
		localInicio: 'Lisboa',
		localTermino: 'Coimbra',
		horaTermino: '14:40',
		dataTermino: '02/05/2024',
		motorista: 'Beatriz Costa',
		cliente: 'Best Transfers 4 U',
	},
	{
		id: '5',
		requisicao: '84214',
		dataInicio: '02/05/2024',
		horaInicio: '14:30',
		localInicio: 'Porto',
		localTermino: 'Braga',
		horaTermino: '15:45',
		dataTermino: '02/05/2024',
		motorista: 'Carlos Sousa',
		cliente: 'Transol Corporate',
	},
	{
		id: '6',
		requisicao: '84215',
		dataInicio: '02/05/2024',
		horaInicio: '16:00',
		localInicio: 'Lisboa',
		localTermino: 'Évora',
		horaTermino: '18:10',
		dataTermino: '02/05/2024',
		motorista: 'Miguel Marques',
		cliente: 'Frota Azul',
	},
]

const CustomModalServicesByLocality: React.FC = () => {
	const { isOpen, closeModal, modalData, modalHandlers } = useModal()
	const data = modalData as ServicesByLocalityModalData | undefined
	const [handleExport, handlePrint] = modalHandlers ?? []

	const [startDate, setStartDate] = useState<Dayjs | null>(
		data?.startDate ? dayjs(data.startDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const [endDate, setEndDate] = useState<Dayjs | null>(
		data?.endDate ? dayjs(data.endDate, 'DD/MM/YYYY') : dayjs('02/05/2024', 'DD/MM/YYYY'),
	)
	const filialOptions = data?.filiais?.length && data.filiais.length > 0 ? data.filiais : ['Transol']
	const serviceOptions = data?.servicos?.length && data.servicos.length > 0 ? data.servicos : ['Todos']
	const localityFilterByOptions =
		data?.localidadeFiltroPorOptions?.length && data.localidadeFiltroPorOptions.length > 0
			? data.localidadeFiltroPorOptions
			: ['Origem / Destino comece por']
	const localityOptions =
		data?.localidades?.length && data.localidades.length > 0 ? data.localidades : ['Lisboa', 'Porto', 'Faro']

	const [filial, setFilial] = useState<string>(data?.filial ?? filialOptions[0])
	const [service, setService] = useState<string>(data?.servico ?? serviceOptions[0])
	const [localityFilterBy, setLocalityFilterBy] = useState<string>(
		data?.localidadeFiltroPor ?? localityFilterByOptions[0],
	)
	const [locality, setLocality] = useState<string>(data?.localidade ?? localityOptions[0])
	const [density, setDensity] = useState<DensityMode>('standard')

	// Use our custom animation hook
	const { animateRows } = useDataGridAnimations(isOpen)

	const columns = useMemo<GridColDef<ServicesByLocalityRow>[]>(
		() => [
			{ field: 'requisicao', headerName: 'Requisição', minWidth: 110 },
			{ field: 'dataInicio', headerName: 'Data Início', minWidth: 100 },
			{ field: 'horaInicio', headerName: 'Hora Início', minWidth: 100 },
			{ field: 'localInicio', headerName: 'Local Início', minWidth: 150, flex: 1 },
			{
				field: 'localTermino',
				headerName: 'Local Término',
				minWidth: 125,
				flex: 1,
			},
			{ field: 'horaTermino', headerName: 'Hora Término', minWidth: 120 },
			{ field: 'dataTermino', headerName: 'Data Término', minWidth: 120 },
			{ field: 'motorista', headerName: 'Motorista', minWidth: 140 },
			{ field: 'cliente', headerName: 'Cliente', minWidth: 120 },
		],
		[],
	)

	const rows = data?.rows?.length ? data.rows : defaultRows

	if (!data) {
		return null
	}

	const handleClearFilters = () => {
		setStartDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setEndDate(dayjs('02/05/2024', 'DD/MM/YYYY'))
		setFilial(filialOptions[0])
		setService(serviceOptions[0])
		setLocalityFilterBy(localityFilterByOptions[0])
		setLocality(localityOptions[0])
	}

	const handleExportRows = () => {
		if (typeof handleExport === 'function') {
			handleExport(rows)
			return
		}
		console.log('Exportar serviços por localidade', rows)
	}

	const handlePrintRows = () => {
		if (typeof handlePrint === 'function') {
			handlePrint(rows)
			return
		}
		console.log('Imprimir serviços por localidade', rows)
	}

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth={false}
			sx={{ pointerEvents: 'auto', zIndex: 2000 }}
			PaperProps={{
				sx: {
					width: 'min(1200px, 96vw)',
					borderRadius: '6px',
					backgroundColor: '#FFFFFF',
					boxShadow: dialogShadow,
					overflow: 'hidden',
					zIndex: 2001,
				},
			}}
		>
			<Box sx={{ fontFamily: 'Poppins, sans-serif', color: '#092056' }}>
				<Box
					sx={{
						padding: '16px 24px 0',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						gap: '16px',
					}}
				>
					<Typography sx={{ fontSize: '16px', fontWeight: 600 }}>Serviços por Localidade</Typography>
					<IconButton
						aria-label='Fechar'
						onClick={closeModal}
						sx={{ padding: '4px' }}
					>
						<CloseRoundedIcon sx={{ fontSize: 20, color: '#00A5FF' }} />
					</IconButton>
				</Box>

				<Box sx={{ padding: '16px 24px 0', display: 'grid', gap: '16px' }}>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<Box
							sx={{
								display: 'grid',
								gridTemplateColumns: { xs: '1fr', md: 'repeat(12, minmax(0, 1fr))' },
								columnGap: '16px',
								rowGap: '12px',
								alignItems: 'end',
							}}
						>
							<Box
								sx={{
									gridColumn: { xs: '1 / -1', md: 'span 4' },
									display: 'grid',
									gridTemplateColumns: '1fr auto 1fr',
									gap: '8px',
									alignItems: 'center',
								}}
							>
								<DatePicker
									label='Data Inicial'
									value={startDate}
									onChange={value => setStartDate(value)}
									format='DD/MM/YYYY'
									slots={{ openPickerButton: EmptyButton }}
									slotProps={{
										textField: { sx: [inputSx, calendarInputSx] },
										...calendarPopperSlotProps,
									}}
								/>
								<Typography sx={{ color: '#092056', fontSize: '16px', lineHeight: 1 }}>-</Typography>
								<DatePicker
									label='Data Final'
									value={endDate}
									onChange={value => setEndDate(value)}
									format='DD/MM/YYYY'
									slots={{ openPickerButton: EmptyButton }}
									slotProps={{
										textField: { sx: [inputSx, calendarInputSx] },
										...calendarPopperSlotProps,
									}}
								/>
							</Box>

							<TextField
								label='Filial'
								value={filial}
								onChange={event => setFilial(event.target.value)}
								select
								sx={[inputSx, { gridColumn: { xs: '1 / -1', md: 'span 2' } }]}
								SelectProps={{
									IconComponent: KeyboardArrowDownIcon,
								}}
							>
								{filialOptions.map(option => (
									<MenuItem
										key={option}
										value={option}
									>
										{option}
									</MenuItem>
								))}
							</TextField>

							<TextField
								label='Serviços'
								value={service}
								onChange={event => setService(event.target.value)}
								select
								sx={[inputSx, { gridColumn: { xs: '1 / -1', md: 'span 2' } }]}
								SelectProps={{
									IconComponent: KeyboardArrowDownIcon,
								}}
							>
								{serviceOptions.map(option => (
									<MenuItem
										key={option}
										value={option}
									>
										{option}
									</MenuItem>
								))}
							</TextField>

							<CustomButton
								variant='text'
								size='small'
								onClick={handleClearFilters}
								sx={{
									gridColumn: { xs: '1 / -1', md: 'span 4' },
									justifySelf: { xs: 'start', md: 'end' },
									color: '#00A5FF',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									textTransform: 'none',
								}}
							>
								Limpar Filtros
							</CustomButton>

							<TextField
								label='Filtrar localidade por'
								value={localityFilterBy}
								onChange={event => setLocalityFilterBy(event.target.value)}
								select
								sx={[inputSx, { gridColumn: { xs: '1 / -1', md: 'span 4' } }]}
								SelectProps={{
									IconComponent: KeyboardArrowDownIcon,
								}}
							>
								{localityFilterByOptions.map(option => (
									<MenuItem
										key={option}
										value={option}
									>
										{option}
									</MenuItem>
								))}
							</TextField>

							<TextField
								label='Localidade'
								value={locality}
								onChange={event => setLocality(event.target.value)}
								select
								sx={[inputSx, { gridColumn: { xs: '1 / -1', md: 'span 8' } }]}
								SelectProps={{
									IconComponent: KeyboardArrowDownIcon,
								}}
							>
								{localityOptions.map(option => (
									<MenuItem
										key={option}
										value={option}
									>
										{option}
									</MenuItem>
								))}
							</TextField>
						</Box>
					</LocalizationProvider>

					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
							flexWrap: 'wrap',
							gap: '12px',
						}}
					>
						<Typography sx={{ fontSize: '24px', fontWeight: 600, lineHeight: '32px' }}>
							{rows.length} Serviços
						</Typography>
						<ToggleButtonGroup
							value={density}
							exclusive
							onChange={(_, next) => next && setDensity(next)}
							sx={toggleGroupSx}
						>
							<ToggleButton value='compact'>
								<TableRowsIcon sx={{ fontSize: 16, color: '#092056' }} />
							</ToggleButton>
							<ToggleButton value='standard'>
								<ViewHeadlineIcon sx={{ fontSize: 16, color: '#092056' }} />
							</ToggleButton>
						</ToggleButtonGroup>
					</Box>

					<AnimatedDataGridPro
						slots={{ columnMenu: CustomColumnMenu }}
						slotProps={{ basePopper: { sx: columnMenuPopperSx } }}
						rows={rows}
						columns={columns}
						density={density}
						animateRows={animateRows}
						checkboxSelection
						columnHeaderHeight={40}
						disableRowSelectionOnClick
						pagination={true}
						pageSize={10}
						sx={[tableSx, servicesTableSx]}
					/>
				</Box>

				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
						gap: '8px',
						padding: '16px 24px',
					}}
				>
					<CustomButton
						variant='text'
						onClick={closeModal}
						sx={popupSecondaryButtonSx}
					>
						Fechar
					</CustomButton>
					<CustomButton
						variant='text'
						onClick={handleExportRows}
						sx={popupSecondaryButtonSx}
					>
						Exportar
					</CustomButton>
					<CustomButton
						variant='contained'
						color='primary'
						onClick={handlePrintRows}
						sx={popupActionButtonSx}
					>
						Imprimir
					</CustomButton>
				</Box>
			</Box>
		</Dialog>
	)
}

export default CustomModalServicesByLocality
